import { type Address } from 'viem'
import {
  Pendle,
  SwapExactTokenForPtRawSuccessResponse,
  RawErrorResponse,
  AddLiquiditySinglePtRawSuccessResponse,
  AddLiquiditySingleTokenRawSuccessResponse,
  RemoveLiquiditySinglePtRawSuccessResponse,
  RemoveLiquiditySingleTokenRawSuccessResponse,
  SwapExactPtForTokenRawSuccessResponse,
} from './interfaces'
import { PendleAggregatorError } from './errors'

export class PendleAggregator implements Pendle {
  private lastCallTimestamp = 0

  constructor(
    private readonly baseUrl: string,
    private readonly maxRetries: number,
  ) {}

  static get builder() {
    return new PendleAggregatorBuilder()
  }

  async swapExactTokenForPt(
    chainId: number,
    receiverAddr: Address,
    marketAddr: Address,
    tokenInAddr: Address,
    amountTokenIn: bigint,
    slippage: number,
    syTokenInAddr?: Address,
    excludedSources?: string,
  ) {
    const baseUrl = `${this.baseUrl}/swapExactTokenForPt`
    const url = this.constructUrl(baseUrl, {
      chainId,
      receiverAddr,
      marketAddr,
      tokenInAddr,
      amountTokenIn,
      slippage,
      syTokenInAddr,
      excludedSources,
    })

    const json = await this.fetchWithRetries<
      SwapExactTokenForPtRawSuccessResponse | RawErrorResponse
    >(url, this.maxRetries)

    if ((json as RawErrorResponse).code) {
      throw new PendleAggregatorError('swapExactTokenForPt')
    }
    const response = json as SwapExactTokenForPtRawSuccessResponse
    return {
      to: response.transaction.to as Address,
      data: response.transaction.data as Address,
      value: BigInt(response.contractCallParams[6].value),
      output: BigInt(response.data.amountPtOut),
    }
  }

  async swapExactPtForToken(
    chainId: number,
    receiverAddr: Address,
    marketAddr: Address,
    tokenOutAddr: Address,
    amountPtIn: bigint,
    slippage: number,
    syTokenOutAddr?: Address,
    excludedSources?: string,
  ) {
    const baseUrl = `${this.baseUrl}/swapExactPtForToken`
    const url = this.constructUrl(baseUrl, {
      chainId,
      receiverAddr,
      marketAddr,
      tokenOutAddr,
      amountPtIn,
      slippage,
      syTokenOutAddr,
      excludedSources,
    })

    const json = await this.fetchWithRetries<
      SwapExactPtForTokenRawSuccessResponse | RawErrorResponse
    >(url, this.maxRetries)

    if ((json as RawErrorResponse).code) {
      throw new PendleAggregatorError('swapExactPtForToken')
    }
    const response = json as SwapExactPtForTokenRawSuccessResponse
    return {
      to: response.transaction.to as Address,
      data: response.transaction.data as Address,
      value: 0n,
      output: BigInt(response.data.amountTokenOut),
    }
  }

  async addLiquiditySinglePt(
    chainId: number,
    receiverAddr: Address,
    marketAddr: Address,
    amountPtIn: bigint,
    slippage: number,
  ) {
    const baseUrl = `${this.baseUrl}/addLiquiditySinglePt`
    const url = this.constructUrl(baseUrl, {
      chainId,
      receiverAddr,
      marketAddr,
      amountPtIn,
      slippage,
    })

    const json = await this.fetchWithRetries<
      AddLiquiditySinglePtRawSuccessResponse | RawErrorResponse
    >(url, this.maxRetries)

    if ((json as RawErrorResponse).code) {
      throw new PendleAggregatorError('addLiquiditySinglePt')
    }
    const response = json as AddLiquiditySinglePtRawSuccessResponse
    return {
      to: response.transaction.to as Address,
      data: response.transaction.data as Address,
      value: BigInt(response.contractCallParams[6].value),
      output: BigInt(response.data.amountLpOut),
    }
  }
  async addLiquiditySingleToken(
    chainId: number,
    receiverAddr: Address,
    marketAddr: Address,
    tokenInAddr: Address,
    amountTokenIn: bigint,
    slippage: number,
    syTokenInAddr?: Address,
  ) {
    const baseUrl = `${this.baseUrl}/addLiquiditySingleToken`
    const url = this.constructUrl(baseUrl, {
      chainId,
      receiverAddr,
      marketAddr,
      tokenInAddr,
      amountTokenIn,
      slippage,
      syTokenInAddr,
    })

    const json = await this.fetchWithRetries<
      AddLiquiditySingleTokenRawSuccessResponse | RawErrorResponse
    >(url, this.maxRetries)

    if ((json as RawErrorResponse).code) {
      throw new PendleAggregatorError('addLiquiditySingleToken')
    }
    const response = json as AddLiquiditySingleTokenRawSuccessResponse
    return {
      to: response.transaction.to as Address,
      data: response.transaction.data as Address,
      value: BigInt(response.contractCallParams[6].value),
      output: BigInt(response.data.amountLpOut),
    }
  }
  async removeLiquiditySinglePt(
    chainId: number,
    receiverAddr: Address,
    marketAddr: Address,
    amountLpToRemove: bigint,
    slippage: number,
  ) {
    const baseUrl = `${this.baseUrl}/removeLiquiditySinglePt`
    const url = this.constructUrl(baseUrl, {
      chainId,
      receiverAddr,
      marketAddr,
      amountLpToRemove,
      slippage,
    })

    const json = await this.fetchWithRetries<
      RemoveLiquiditySinglePtRawSuccessResponse | RawErrorResponse
    >(url, this.maxRetries)

    if ((json as RawErrorResponse).code) {
      throw new PendleAggregatorError('removeLiquiditySinglePt')
    }
    const response = json as RemoveLiquiditySinglePtRawSuccessResponse
    return {
      to: response.transaction.to as Address,
      data: response.transaction.data as Address,
      value: 0n,
      output: BigInt(response.data.amountPtOut),
    }
  }
  async removeLiquiditySingleToken(
    chainId: number,
    receiverAddr: Address,
    marketAddr: Address,
    amountLpToRemove: bigint,
    tokenOutAddr: Address,
    slippage: number,
    syTokenOutAddr?: Address,
  ) {
    const baseUrl = `${this.baseUrl}/removeLiquiditySingleToken`
    const url = this.constructUrl(baseUrl, {
      chainId,
      receiverAddr,
      marketAddr,
      amountLpToRemove,
      tokenOutAddr,
      slippage,
      syTokenOutAddr,
    })

    const json = await this.fetchWithRetries<
      RemoveLiquiditySingleTokenRawSuccessResponse | RawErrorResponse
    >(url, this.maxRetries)

    if ((json as RawErrorResponse).code) {
      throw new PendleAggregatorError('removeLiquiditySingleToken')
    }
    const response = json as RemoveLiquiditySingleTokenRawSuccessResponse
    return {
      to: response.transaction.to as Address,
      data: response.transaction.data as Address,
      value: 0n,
      output: BigInt(response.data.amountTokenOut),
    }
  }

  private async fetchWithRetries<T>(url: string, maxRetries: number): Promise<T> {
    let retries = 0
    let json: T | undefined = undefined
    while (retries < maxRetries) {
      try {
        json = await this.fetch<T>(url)
        break
      } catch (e) {
        retries++
      }
    }

    if (json === undefined) throw new Error(`Failed to fetch quote after ${maxRetries} retries`)

    return json
  }

  private async fetch<T>(url: string): Promise<T> {
    const now = Date.now()
    if (this.lastCallTimestamp !== 0 && now - this.lastCallTimestamp < 1000) {
      await sleep(1000)
    }

    this.lastCallTimestamp = now + 1000

    return await fetch(url).then(async (res) => {
      if (!res.ok) {
        throw new Error(`Failed to fetch quote: ${res.statusText}`)
      }
      return (await res.json()) as T
    })
  }

  private constructUrl(
    baseUrl: string,
    params: Record<string, string | number | bigint | Address | undefined>,
  ): string {
    const queryParams = Object.entries(params)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')
    return `${baseUrl}?${queryParams}`
  }
}

class PendleAggregatorBuilder {
  private baseUrl = 'https://api-v2.pendle.finance/sdk/api/v1'
  private maxRetries = 3

  withBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl
    return this
  }

  withMaxRetries(maxRetries: number) {
    this.maxRetries = maxRetries
    return this
  }

  build() {
    return new PendleAggregator(this.baseUrl, this.maxRetries)
  }
}

/**
 * Delays the execution of the code for a specified number of milliseconds.
 *
 * @param ms - The number of milliseconds to delay
 * @returns A Promise that resolves after the specified delay
 */
export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))
